import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIntegrationProcesses } from 'services/repositories/integrations/integrations';
import { IntegrationDTO } from 'support/types';
import { buildDocumentTypeFilter } from 'components/Filters/helpers';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

type ImportDocumentsTargetPickerProps = {
  integrations: Array<IntegrationDTO>;
  selectedIntegrationId?: string;
  onSelectIntegration: (integrationId: string | undefined) => void;
  selectedProcessId?: string;
  onSelectProcess: (processId: string | undefined) => void;
};

/**
 * Lets the user choose which source system (integration) and document type (source process) to
 * import into when the modal isn't opened with a pre-resolved target. Auto-selects when there is
 * exactly one option so the common single-integration partner gets a near one-click flow.
 */
export const ImportDocumentsTargetPicker = ({
  integrations,
  selectedIntegrationId,
  onSelectIntegration,
  selectedProcessId,
  onSelectProcess,
}: ImportDocumentsTargetPickerProps) => {
  const { t } = useTranslation();
  const documentTypeLabels = useDocumentTypeLabels();

  useEffect(() => {
    if (!selectedIntegrationId && integrations.length === 1) {
      onSelectIntegration(integrations[0].id);
    }
  }, [integrations, selectedIntegrationId, onSelectIntegration]);

  const { data: processes } = useIntegrationProcesses({ variables: { id: selectedIntegrationId } });
  const sourceProcesses = useMemo(() => (processes ?? []).filter((process) => process.type === 'SOURCE'), [processes]);

  useEffect(() => {
    if (selectedIntegrationId && !selectedProcessId && sourceProcesses.length === 1) {
      onSelectProcess(sourceProcesses[0].id);
    }
  }, [sourceProcesses, selectedIntegrationId, selectedProcessId, onSelectProcess]);

  const noSourceProcesses = Boolean(selectedIntegrationId) && processes !== undefined && sourceProcesses.length === 0;

  // Hide the source-system picker when there's only one option — it's auto-selected above and a
  // single-choice dropdown is just noise for the common single-integration partner.
  const showIntegrationPicker = integrations.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {showIntegrationPicker && (
        <Dropdown<string>
          label={t('transactions:list.importDocuments.picker.integration.label')}
          emptySelectionLabel={t('transactions:list.importDocuments.picker.integration.placeholder')}
          value={selectedIntegrationId ?? ''}
          onChange={(value) => {
            onSelectIntegration(value || undefined);
            onSelectProcess(undefined);
          }}
          options={integrations.map((integration) => ({ value: integration.id, label: integration.name }))}
        />
      )}
      <Dropdown<string>
        label={t('transactions:list.importDocuments.picker.process.label')}
        emptySelectionLabel={t('transactions:list.importDocuments.picker.process.placeholder')}
        value={selectedProcessId ?? ''}
        disabled={!selectedIntegrationId}
        onChange={(value) => onSelectProcess(value || undefined)}
        options={sourceProcesses.map((process) => ({
          value: process.id,
          label: documentTypeLabels.get(process.messageType) ?? process.messageType,
        }))}
        description={noSourceProcesses ? t('transactions:list.importDocuments.picker.process.empty') : undefined}
      />
    </div>
  );
};

// Reuse the document-type labels already declared statically in the filter helper so the
// process options read like the rest of the app — and so we don't introduce a template-literal
// `t('common:messageTypes.singular.${type}')` key that the i18next parser can't follow.
const useDocumentTypeLabels = (): Map<string, string> =>
  useMemo(() => {
    const options = buildDocumentTypeFilter('message_type').dropDownOptions.dropdownValues;
    return new Map(options.map((option) => [option.value as string, option.label]));
  }, []);
